import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/ApiManager';
import { theme } from '../utils/theme';

const InvoicingStep = ({ onComplete, bucketId }) => {
  const { t } = useTranslation('invoice');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Partner information
    partnerName: '',
    partnerTaxNumber: '',
    partnerAddress: '',
    partnerCity: '',
    partnerPostCode: '',
    partnerCountry: 'HU',
    
    // Fixed invoice details
    currency: 'EUR',
    language: 'hu',
    paymentMethod: 'bankcard',
    fulfillmentDate: new Date().toISOString().split('T')[0],
    dueDate: new Date().toISOString().split('T')[0],
  });
  const [debugInfo, setDebugInfo] = useState({});

  useEffect(() => {
    let mounted = true;
    
    const loadInvoiceData = async () => {
      try {
        // First, try to load from sessionStorage to have immediate data
        const lastInvoiceDataStr = sessionStorage.getItem('lastInvoiceData');
        if (lastInvoiceDataStr && mounted) {
          const parsedData = JSON.parse(lastInvoiceDataStr);
          const invoiceData = parsedData.invoice;
          
          if (invoiceData) {
            setFormData(prevData => ({
              ...prevData,
              partnerName: invoiceData.partnerName || '',
              partnerTaxNumber: invoiceData.partnerTaxNumber || '',
              partnerAddress: invoiceData.partnerAddress || '',
              partnerCity: invoiceData.partnerCity || '',
              partnerPostCode: invoiceData.partnerPostCode || '',
              partnerCountry: invoiceData.partnerCountry || 'HU',
              currency: invoiceData.currency || 'EUR',
              language: invoiceData.language || 'hu',
              paymentMethod: invoiceData.paymentMethod || 'bankcard',
              fulfillmentDate: new Date().toISOString().split('T')[0],
              dueDate: new Date().toISOString().split('T')[0],
            }));
          }
        }

        // Then, if we have a bucketId, try to get bucket-specific invoice
        if (bucketId && mounted) {
          const response = await api.get(`/api/invoice/bucket/${bucketId}`);
          if (response.data.invoice && mounted) {
            setFormData(prevData => ({
              ...prevData,
              ...response.data.invoice,
              fulfillmentDate: new Date().toISOString().split('T')[0],
              dueDate: new Date().toISOString().split('T')[0],
            }));
          }
        }
      } catch (error) {
        console.error('Error loading invoice data:', error);
      }
    };

    loadInvoiceData();

    return () => {
      mounted = false;
    };
  }, [bucketId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/api/invoice', {
        ...formData,
        bucketId
      });

      if (response.data.invoiceId) {
        // Store the invoice data in session storage for future use
        sessionStorage.setItem('lastInvoiceData', JSON.stringify({
          invoice: formData,
          timestamp: new Date().toISOString()
        }));

        // Call onComplete with the form data
        await onComplete(formData);
      } else {
        throw new Error('Failed to create invoice');
      }
    } catch (error) {
      alert(t('errors.saveError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-xl mx-auto">
        <div className={`${theme.card.base} bg-opacity-75 p-6`}>
          <div className="mb-8">
            <h2 className={theme.text.h2 + ' text-[#6D9773]'}>{t('title')}</h2>
          </div>

          {/* Debug info - remove in production */}
          {Object.keys(debugInfo).length > 0 && (
            <div className="mb-4 p-2 bg-gray-100 rounded-md text-xs overflow-auto">
              <p className="font-bold">Debug: Last Invoice Data Structure</p>
              <pre>{JSON.stringify(debugInfo, null, 2)}</pre>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <h3 className={theme.text.h3 + ' mb-4 text-[#6D9773]'}>{t('sections.partnerInfo')}</h3>
              
              <div>
                <label className="block text-sm font-medium text-[#6D9773] mb-1">
                  {t('fields.companyName.label')}
                </label>
                <input
                  type="text"
                  name="partnerName"
                  value={formData.partnerName}
                  onChange={handleChange}
                  className={theme.input.base + ' bg-white/90 text-gray-900 border-white/30 placeholder-gray-500'}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#6D9773] mb-1">
                  {t('fields.taxNumber.label')}
                </label>
                <input
                  type="text"
                  name="partnerTaxNumber"
                  value={formData.partnerTaxNumber}
                  onChange={handleChange}
                  className={theme.input.base + ' bg-white/90 text-gray-900 border-white/30 placeholder-gray-500'}
                  placeholder={t('fields.taxNumber.placeholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#6D9773] mb-1">
                  {t('fields.address.label')}
                </label>
                <input
                  type="text"
                  name="partnerAddress"
                  value={formData.partnerAddress}
                  onChange={handleChange}
                  className={theme.input.base + ' bg-white/90 text-gray-900 border-white/30 placeholder-gray-500'}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#6D9773] mb-1">
                    {t('fields.city.label')}
                  </label>
                  <input
                    type="text"
                    name="partnerCity"
                    value={formData.partnerCity}
                    onChange={handleChange}
                    className={theme.input.base + ' bg-white/90 text-gray-900 border-white/30 placeholder-gray-500'}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#6D9773] mb-1">
                    {t('fields.postCode.label')}
                  </label>
                  <input
                    type="text"
                    name="partnerPostCode"
                    value={formData.partnerPostCode}
                    onChange={handleChange}
                    className={theme.input.base + ' bg-white/90 text-gray-900 border-white/30 placeholder-gray-500'}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#6D9773] mb-1">
                  {t('fields.country.label')}
                </label>
                <input
                  type="text"
                  name="partnerCountry"
                  value={formData.partnerCountry}
                  onChange={handleChange}
                  className={theme.input.base + ' bg-white/90 text-gray-900 border-white/30 placeholder-gray-500'}
                  required
                />
              </div>
            </div>

            <div className="mt-8">
              <button
                type="submit"
                disabled={loading}
                className={`${theme.button.base} ${theme.button.cta} ${theme.button.sizes.lg} w-full ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {loading ? t('buttons.processing') : t('buttons.proceedToPayment')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InvoicingStep;